import React, { useState, useEffect } from "react";
import { flushSync } from "react-dom";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import ChecklistIcon from "@mui/icons-material/Checklist";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoIcon from "@mui/icons-material/Info";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import useEmblaCarousel from "embla-carousel-react";
import { toast } from "react-toastify";
import RenderQuestionDragAndDrop from "./RenderQuestionDragAndDrop";
import QuizReviewModal from "./QuizReviewModal";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_learning_quiz_detail } from "../../../constants";
import {
  BRAND_VIOLET,
  usePrefersReducedMotion,
  vibrate,
} from "../take_training_employee/swipe_deck_player/cards/cardCommon";
import { useNotifyMobileModal } from "../take_training_employee/useNotifyMobileModal";

// Returns a copy, since the preview path passes the quiz editor's live state.
// Run once per attempt, when the quiz loads, so the order holds until it ends.
const withAttemptQuestionOrder = (quiz) => {
  if (!quiz?.shuffle_questions || !quiz.learning_questions) return quiz;
  const questions = [...quiz.learning_questions];
  for (let i = questions.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [questions[i], questions[j]] = [questions[j], questions[i]];
  }
  return { ...quiz, learning_questions: questions };
};

function CompleteQuizModal(props) {
  useNotifyMobileModal(props.open);

  const t = (english, spanish, chinese) => {
    if (props.language === "chinese") return chinese;
    if (props.language === "spanish") return spanish;
    return english;
  };

  const questionCountLabel = (count) => {
    if (props.language === "chinese") return `${count} 道题`;
    if (props.language === "spanish") return `${count} ${count === 1 ? "pregunta" : "preguntas"}`;
    return `${count} ${count === 1 ? "question" : "questions"}`;
  };

  // mobilePreview === true sizes the modal as a 9:16 phone frame
  // (mirrors SwipeDeckPlayer's frame at line 411) so authors can verify
  // the small-screen layout on a desktop viewport. The viewport-keyed
  // Tailwind `sm:` (≤639px) won't fire at this container width — class
  // ternaries throughout this file flip the relevant chrome blocks
  // explicitly when mobilePreview is on.
  const mobilePreview = props.mobilePreview === true;

  const style = mobilePreview
    ? {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        height: "90vh",
        maxHeight: "900px",
        aspectRatio: "9 / 16",
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: "background.paper",
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.4)",
      }
    : {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "100%",
        height: "100%",
        bgcolor: "background.paper",
        border: "2px solid #E2E8F0",
        boxShadow: 24,
        overflowY: "auto",
      };

  const [shake, setShake] = useState(false);

  // Track the shake-reset timer so we can clear it if the modal
  // unmounts mid-animation. Without this, React logs an "update on
  // unmounted component" warning when the timeout fires after teardown.
  const shakeTimerRef = React.useRef(null);
  useEffect(() => {
    return () => {
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    };
  }, []);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Polish layer (motion / confetti / haptics) — borrowed from the swipe
  // deck player so quizzes feel as snappy. The visual UI is unchanged;
  // everything below is additive.

  const reducedMotion = usePrefersReducedMotion();
  const questionContainerRef = React.useRef(null);
  const confettiFnRef = React.useRef(null);
  const hasFiredCompletionConfettiRef = React.useRef(false);

  // Bind canvas-confetti to a canvas inside this modal. Use a callback
  // ref instead of useEffect because MUI Modal lazily mounts its
  // children — when CompleteQuizModal first renders with open=false,
  // the canvas isn't in the DOM and a `[]`-deps effect would bail and
  // never re-fire. The callback ref runs exactly when the canvas
  // attaches and detaches, so binding tracks the actual node lifetime.
  // Reset to null on detach so the next open creates a fresh binding
  // rather than holding a stale reference to an unmounted node.
  const setCanvasRef = React.useCallback((node) => {
    if (node) {
      confettiFnRef.current = confetti.create(node, {
        resize: true,
        useWorker: true,
      });
    } else {
      confettiFnRef.current = null;
    }
  }, []);

  const fireBigConfetti = React.useCallback(() => {
    if (reducedMotion || !confettiFnRef.current) return;
    confettiFnRef.current({
      particleCount: 90,
      spread: 75,
      origin: { y: 0.55 },
      colors: [BRAND_VIOLET, "#ffffff", "#fde68a", "#fbcfe8"],
      ticks: 200,
    });
  }, [reducedMotion]);

  const [isLoading, setIsLoading] = useState(
    props.isLoading ? props.isLoading : false
  );
  const [quizDetails, setQuizDetails] = useState(props.modalDataQuiz || null);
  const [step, setStep] = useState("welcome");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [questionResponses, setQuestionResponses] = useState([]);
  const [attemptUuid, setAttemptUuid] = useState(null);
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [isSubmittingResults, setIsSubmittingResults] = useState(false);

  // pass_percentage arrives as a real integer from the backend, but the quiz
  // editor's Preview path passes live in-memory state where it's a string from
  // a text input. Coerce so the `> 0` guard is honest and `.toFixed()` is safe.
  const quizPassPercentage = Number(quizDetails?.pass_percentage) || 0;

  const modalRef = React.useRef(null);

  const scrollToTop = () => {
    if (modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Embla carousel — wraps the welcome/question/completed slides so the
  // user can swipe vertically (touch + mouse drag), matching the swipe
  // deck player's feel. State stays bidirectional: existing handlers
  // (handleStartQuiz / handleProceed / etc.) keep calling setStep +
  // setCurrentQuestionIndex; an effect mirrors that to embla via
  // scrollTo. Embla swipes fire on('select'), which mirrors back to the
  // state setters. Forward-only — backward swipes are snapped forward
  // because previously-submitted questions can't be re-attempted.

  const slides = React.useMemo(() => {
    if (!quizDetails) return [{ kind: "welcome" }];
    const questions = quizDetails.learning_questions || [];
    if (questions.length === 0) {
      return [{ kind: "welcome" }, { kind: "no-questions" }];
    }
    return [
      { kind: "welcome" },
      ...questions.map((_, idx) => ({ kind: "question", idx })),
      { kind: "completed" },
    ];
  }, [quizDetails]);

  // watchDrag is a per-drag-attempt function — embla calls it on
  // pointerdown to decide whether to capture the gesture. Reading from a
  // ref keeps the function stable while still seeing fresh state.
  const dragGateRef = React.useRef({ step, hasSubmitted });
  useEffect(() => {
    dragGateRef.current = { step, hasSubmitted };
  }, [step, hasSubmitted]);
  const watchDragHandler = React.useCallback(() => {
    const { step: s, hasSubmitted: hs } = dragGateRef.current;
    if (s === "welcome") return true;
    if (s === "question") return hs;
    return false; // completed slide is the terminal state
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      axis: "y",
      containScroll: "trimSnaps",
      watchDrag: watchDragHandler,
    },
    [],
  );

  // ReInit when the slide count changes (quizDetails loads, retake clears
  // questions, etc.) — embla doesn't auto-detect added/removed slides.
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
  }, [emblaApi, slides.length]);

  // State → embla: when step/currentQuestionIndex change (via clicks),
  // mirror to embla. Comparing against selectedScrollSnap avoids a loop
  // when the on('select') handler already brought state into sync.
  useEffect(() => {
    if (!emblaApi) return;
    let target = 0;
    if (step === "welcome") target = 0;
    else if (step === "completed") target = slides.length - 1;
    else target = 1 + currentQuestionIndex;
    if (target < 0 || target >= slides.length) return;
    if (emblaApi.selectedScrollSnap() !== target) {
      emblaApi.scrollTo(target);
    }
  }, [emblaApi, step, currentQuestionIndex, slides.length]);

  // Embla → state: when the user swipes, mirror to state. Forward-only:
  // if they swipe back (lower idx), snap forward. Entering a fresh
  // question slide also resets the per-question transient state
  // (selectedAnswers / feedback / hasSubmitted) so a new question is
  // never pre-filled with the previous one's selections.
  //
  // The handler reads `step` and `currentQuestionIndex` via refs (not
  // closure) because this effect re-attaches only when slides change.
  // Without the refs, the failure-retry path that scrolls back to the
  // last question would compare the current slide's idx against a
  // stale `currentQuestionIndex` from effect-attach time, conclude
  // we're entering a "new" question, and clear selectedAnswers /
  // hasSubmitted — forcing the user to re-answer and producing a
  // duplicate questionResponses entry on the next submit.
  const lastSlideIdxRef = React.useRef(0);
  const stepRef = React.useRef(step);
  const currentQuestionIndexRef = React.useRef(currentQuestionIndex);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  useEffect(() => {
    currentQuestionIndexRef.current = currentQuestionIndex;
  }, [currentQuestionIndex]);
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const idx = emblaApi.selectedScrollSnap();
      if (idx < lastSlideIdxRef.current) {
        emblaApi.scrollTo(lastSlideIdxRef.current, true);
        return;
      }
      lastSlideIdxRef.current = idx;
      const slide = slides[idx];
      if (!slide) return;
      const liveStep = stepRef.current;
      const liveCurrentQuestionIndex = currentQuestionIndexRef.current;
      if (slide.kind === "welcome") {
        if (liveStep !== "welcome") setStep("welcome");
      } else if (slide.kind === "question") {
        if (liveStep !== "question") setStep("question");
        if (liveCurrentQuestionIndex !== slide.idx) {
          // flushSync forces React to commit these updates before
          // onSelect returns, which closes the window where the new
          // slide is visually present but still rendering as
          // renderInactiveQuestion (no onClick). Without this, a fast
          // tap right after a swipe hits a non-clickable div and
          // silently does nothing — the user has to tap a second
          // time. Embla's `select` runs in a synchronous event
          // handler so flushSync is safe here; the perf cost (one
          // batched update per swipe) is invisible.
          flushSync(() => {
            setCurrentQuestionIndex(slide.idx);
            setSelectedAnswers([]);
            setFeedback(null);
            setHasSubmitted(false);
          });
        }
      } else if (slide.kind === "completed") {
        if (liveStep !== "completed") setStep("completed");
        // Both DnD and regular quizzes route through recordAttemptIfNeeded
        // — DnD's final-question CTA awaits it directly (so its failure
        // surfaces a toast and doesn't advance), and on('select') here
        // catches the swipe-to-completed path. The shared guard
        // (attemptRecordedRef) makes the second call a no-op when one
        // path already succeeded.
        //
        // Mirror the Next button's failure gate: if persistence fails,
        // bounce the user back to the last question so they can retry
        // instead of sitting on a "completed" screen with no saved
        // attempt. The toast inside recordAttemptIfNeeded surfaces the
        // error; this scroll gives the user somewhere recoverable.
        recordAttemptIfNeededRef.current().then((ok) => {
          if (ok) return;
          const lastQuestionSlideIdx = slides.length - 2;
          // For non-empty quizzes the slide before "completed" is the
          // final question. (For empty quizzes there's no completed
          // slide so this branch never runs in the first place.)
          if (lastQuestionSlideIdx < 1) return;
          // Lower the forward-only guard before scrolling back —
          // otherwise on('select') would see the lower idx as a
          // backward swipe and immediately snap us forward again.
          lastSlideIdxRef.current = lastQuestionSlideIdx;
          emblaApi.scrollTo(lastQuestionSlideIdx, true);
        });
      }
    };
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, slides]);

  // Reset embla cursor + attempt-record guards when the modal opens.
  // The existing reset effect handles step state; these refs are
  // ref-tracked side-channels that don't trigger React renders.
  //
  // Two refs, not one:
  //   - attemptRecordedRef: flips true ONLY after a successful submit.
  //     Subsequent callers (e.g., the on('select') swipe path firing
  //     after a button click already succeeded) short-circuit cleanly.
  //   - attemptPromiseRef: tracks an in-flight submission. A second
  //     caller while the first is pending awaits the same promise
  //     instead of seeing an early `true` and advancing past the
  //     completion screen before persistence resolved. Cleared on both
  //     success and failure so the next call either short-circuits
  //     (success: ref was flipped true) or fires a fresh attempt
  //     (failure: retry path).
  const attemptRecordedRef = React.useRef(false);
  const attemptPromiseRef = React.useRef(null);
  useEffect(() => {
    if (props.open) {
      lastSlideIdxRef.current = 0;
      attemptRecordedRef.current = false;
      attemptPromiseRef.current = null;
    }
  }, [props.open]);

  // Records the quiz attempt to the backend. Both the explicit Next /
  // Results button and the swipe-from-last-question path funnel through
  // here.
  //
  // Concurrency: a fast double-click on the Results button used to set
  // attemptRecordedRef = true synchronously before the await, so the
  // second handler invocation hit the early `return true` while the
  // first request was still pending — and called handleProceed() to
  // advance the user to completed BEFORE persistence resolved. If the
  // original then failed, the user had already left the retry zone
  // with no saved attempt. The fix: only flip attemptRecordedRef on
  // SUCCESS, and dedupe overlapping calls onto a single in-flight
  // promise so the second caller awaits the real outcome.
  //
  // Failure handling: a transient API/network error must not lock the
  // user out of retrying. The promise resolves to false (not a throw)
  // so the call site can gate advancement, and a toast surfaces the
  // error. Subsequent calls fire a fresh attempt.
  //
  // Returns true on success / no-op (preview / empty quiz / already
  // recorded), false when the call rejected.
  const recordAttemptIfNeeded = React.useCallback(() => {
    if (attemptRecordedRef.current) return Promise.resolve(true);
    if (props.type === "Preview") return Promise.resolve(true);
    if (!quizDetails?.learning_questions?.length) return Promise.resolve(true);
    if (attemptPromiseRef.current) return attemptPromiseRef.current;

    setIsSubmittingResults(true);
    const promise = (async () => {
      try {
        const passScore =
          (score / quizDetails.learning_questions.length) * 100;
        const passed = passScore >= (quizPassPercentage ?? 0);
        // silent: true — we'll fire our own toast based on the returned
        // error_message (green for "already passed", red for other
        // failures), so the helper should not double-toast.
        const result = await props.post_learning_block_user_passed(
          quizDetails.uuid,
          passed,
          props.employeeID,
          passScore,
          questionResponses,
          { silent: true },
        );
        // post_learning_block_user_passed catches its own errors and
        // resolves with an object (either {attempt_uuid, ...} on success or
        // {error_message} on failure) instead of throwing — so the catch
        // block here never fires on a real network/API failure. Treat a
        // result without attempt_uuid as a failure so attemptRecordedRef
        // stays false, the user can retry, and we don't silently advance
        // them to the completed screen with no saved attempt.
        if (!result || !result.attempt_uuid) {
          // The backend rejects re-submissions of an already-passed quiz
          // with "You have already passed." That's not a real error to the
          // user — they did pass — so surface it as a calm success toast
          // (green) rather than the red "couldn't save" toast, AND treat
          // the call as a success so the caller (the Results-button gate
          // and the swipe-to-completion path) advances to the completion
          // screen instead of bouncing the user back to the last question.
          const errorMessage = result?.error_message || "";
          if (/already passed/i.test(errorMessage)) {
            toast.success(
              t(
                "You already passed this quiz",
                "Ya aprobaste este cuestionario",
                "你已通过此测验",
              ),
            );
            attemptRecordedRef.current = true;
            return true;
          }
          // Retrying can never fix a used-up test-out quiz, so say what's true.
          if (quizDetails?.gate_on_pass === false && /attempts/i.test(errorMessage)) {
            toast.info(errorMessage);
            attemptRecordedRef.current = true;
            props.handleClose();
            props.setRefreshSection(true);
            return false;
          }
          toast.error(
            t(
              "Couldn't save your quiz attempt. Please try again.",
              "No se pudo guardar tu intento. Inténtalo de nuevo.",
              "无法保存测验结果。请再试一次。",
            ),
          );
          return false;
        }
        setAttemptUuid(result.attempt_uuid);
        if (result.score !== undefined) {
          const backendScore = Math.round(
            (result.score / 100) * quizDetails.learning_questions.length,
          );
          setScore(backendScore);
        }
        attemptRecordedRef.current = true;
        return true;
      } catch (error) {
        // Defensive: if post_learning_block_user_passed is ever updated
        // to rethrow, this still surfaces the error and allows retry.
        toast.error(
          t(
            "Couldn't save your quiz attempt. Please try again.",
            "No se pudo guardar tu intento. Inténtalo de nuevo.",
            "无法保存测验结果。请再试一次。",
          ),
        );
        return false;
      } finally {
        setIsSubmittingResults(false);
        attemptPromiseRef.current = null;
      }
    })();
    attemptPromiseRef.current = promise;
    return promise;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    score,
    quizDetails,
    quizPassPercentage,
    questionResponses,
    props,
  ]);

  // The on('select') embla effect attaches once per slides change and
  // would otherwise capture a stale recordAttemptIfNeeded (score and
  // questionResponses change throughout the attempt — without this ref
  // the swipe-to-completed path would record score=0). Reading via the
  // ref guarantees the latest closure is invoked.
  const recordAttemptIfNeededRef = React.useRef(recordAttemptIfNeeded);
  useEffect(() => {
    recordAttemptIfNeededRef.current = recordAttemptIfNeeded;
  }, [recordAttemptIfNeeded]);

  // console.log("QUIZ DETAILS", quizDetails);

  const fetchQuizDetail = async (showLoading) => {
    setIsLoading(showLoading);
    let api_params = {
      company_id: props.homeCompany,
      learning_quiz_uuid: props.quizSelected?.uuid,
      store_team_member_id: props.employeeID ? props.employeeID : null,
      language: props.language
    };

    try {
      const res = await getRightworkAPI(
        API_get_learning_quiz_detail,
        api_params
      );
      if (res.status === 200) {
        setQuizDetails(withAttemptQuestionOrder(res.data.redux?.payload?.learning_quiz));
        setIsLoading(false);
      }
    } catch (error) {
      throw Error(error);
    }
  };

  useEffect(() => {
    setIsLoading(props.isLoading);
  }, [props.isLoading]);

  // Reset quiz state when quiz changes or modal opens
  useEffect(() => {
    if (props.open) {
      // Reset all state variables
      setStep("welcome");
      setCurrentQuestionIndex(0);
      setSelectedAnswers([]);
      setScore(0);
      setFeedback(null);
      setHasSubmitted(false);
      setQuestionResponses([]);
      setAttemptUuid(null);
      setOpenReviewModal(false);
      setIsSubmittingResults(false);

      // Fetch or set quiz details
      if (!props.modalDataQuiz && props.quizSelected) {
        fetchQuizDetail(true);
      } else {
        setQuizDetails(withAttemptQuestionOrder(props.modalDataQuiz));
      }
    }
  }, [props.open, props.quizSelected?.uuid, props.modalDataQuiz?.uuid]);

  const handleNextQuestion = () => {
    const question = quizDetails.learning_questions[currentQuestionIndex];
    const correctAnswers = question.answers
      .filter(([, is_correct]) => is_correct)
      .map(([answer, , image_uuid]) => image_uuid || answer);

    const isCorrect =
      correctAnswers.every((id) => selectedAnswers.includes(id)) &&
      selectedAnswers.every((id) => correctAnswers.includes(id));

    if (isCorrect) {
      setScore(score + 1);
    }

    // Record the question response
    const selectedIndices = question.answers
      .map(([answer, , image_uuid], index) => {
        const answerId = image_uuid || answer;
        return selectedAnswers.includes(answerId) ? index : null;
      })
      .filter((index) => index !== null);

    const questionResponse = {
      question_uuid: question.uuid,
      selected_answer_indices: selectedIndices,
      is_correct: isCorrect,
    };

    setQuestionResponses([...questionResponses, questionResponse]);

    setFeedback(isCorrect ? "correct" : "incorrect");
    setHasSubmitted(true);

    // Polish: haptic + chip-color flash on right, shake + haptic on
    // wrong. Per-question confetti was tried but reads as glitchy when
    // the completion-screen burst follows shortly after — the chip
    // color and haptic alone are enough to confirm the answer, and the
    // big celebration is reserved for the completion moment.
    if (isCorrect) {
      vibrate([15, 40, 15]);
    } else {
      setShake(true);
      vibrate(40);
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
      shakeTimerRef.current = setTimeout(() => setShake(false), 450);
    }
  };

  const handleProceed = () => {
    setFeedback(null);
    setSelectedAnswers([]);
    setHasSubmitted(false); // Reset the submission state for the next question
    if (currentQuestionIndex < quizDetails.learning_questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setStep("completed");
    }
    scrollToTop();
  };

  const handleStartQuiz = () => {
    setStep("question");
    vibrate(8);
  };

  // Completion celebration — fire confetti + a triple-pulse haptic when
  // the user lands on the results screen, but only if they passed.
  // Failing the quiz isn't a moment to celebrate. The ref guard prevents
  // re-firing if the completed step re-renders.
  useEffect(() => {
    if (step !== "completed") {
      hasFiredCompletionConfettiRef.current = false;
      return;
    }
    if (hasFiredCompletionConfettiRef.current) return;
    if (!quizDetails?.learning_questions?.length) return;
    const passScore = (score / quizDetails.learning_questions.length) * 100;
    const passed = passScore >= (quizPassPercentage ?? 0);
    if (!passed) return;
    hasFiredCompletionConfettiRef.current = true;
    fireBigConfetti();
    vibrate([20, 30, 60]);
  }, [step, score, quizDetails, quizPassPercentage, fireBigConfetti]);

  // (Manual swipe-up handler was removed when embla took over — embla's
  // own drag detection handles touch + mouse drag, gated by watchDrag.)

  // Embla swallows the next click after any drag (its accidental-click
  // guard), so onClick on in-carousel elements eats the user's first
  // tap-after-swipe. Pointer events fire before the synthesized click
  // and aren't subject to that guard, but they also fire for drags
  // themselves AND for any pointer button release (right-click,
  // middle-click) — so we (1) record pointer-down coords and only
  // fire on pointer-up if movement was below a tap threshold (~10px),
  // and (2) gate to primary releases via e.button === 0 so right-
  // click context menus and middle-clicks don't trigger answer
  // selection. Single shared ref is fine because pointer-down updates
  // it before pointer-up reads it; one-finger taps don't overlap on
  // touch. button === 0 is also the value pointer events report for
  // touch contact, so mobile is unaffected.
  const tapStartRef = React.useRef(null);
  const handleTapStart = (e) => {
    if (e.button !== 0) return;
    tapStartRef.current = { x: e.clientX, y: e.clientY };
  };
  const handleTapEnd = (action) => (e) => {
    const start = tapStartRef.current;
    tapStartRef.current = null;
    if (e.button !== 0) return;
    if (!start) return;
    const dx = Math.abs(e.clientX - start.x);
    const dy = Math.abs(e.clientY - start.y);
    if (dx < 10 && dy < 10) action(e);
  };

  const handleAnswerSelect = (answer, image_uuid) => {
    if (hasSubmitted) return; // Prevent selecting answers after submission
    const answerId = image_uuid || answer; // Use image_uuid if available, otherwise use the answer text
    setSelectedAnswers((prevSelected) =>
      prevSelected.includes(answerId)
        ? prevSelected.filter((id) => id !== answerId)
        : [...prevSelected, answerId]
    );
  };

  const renderWelcome = () => (
    <div className="w-full h-full flex flex-col items-center justify-center p-6">
      {/* Icon Badge */}
      <div className="w-20 h-20 rounded-full bg-violet-100 flex items-center justify-center">
        <div className="text-violet-700">
          <SchoolOutlinedIcon style={{ fontSize: "48px" }} />
        </div>
      </div>

      {/* Quiz Title */}
      <h3 className="text-4xl text-slate-900 font-bold mt-6 text-center max-w-2xl">
        {quizDetails?.name}
      </h3>

      {/* Quiz Details Card */}
      <div className="mt-8 bg-slate-50 rounded-xl p-6 w-full max-w-md">
        <div className="space-y-4">
          {/* Questions Count */}
          <div className="flex items-center gap-x-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <div className="text-violet-700">
                <ChecklistIcon style={{ fontSize: "20px" }} />
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">
                {t("Questions", "Preguntas", "题目")}
              </p>
              <p className="text-lg font-semibold text-slate-900">
                {questionCountLabel(quizDetails?.learning_questions?.length)}
              </p>
            </div>
          </div>

          {/* Pass Percentage */}
          {quizDetails?.pass_percentage !== 0 && (
            <div className="flex items-center gap-x-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <div className="text-emerald-700">
                  <FlagOutlinedIcon style={{ fontSize: "20px" }} />
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">
                  {t("Pass Percentage", "Porcentaje de aprobación", "通过分数")}
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  {quizDetails?.pass_percentage}%
                </p>
              </div>
            </div>
          )}

          {/* Attempts */}
          <div className="flex items-center gap-x-3">
            <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
              <div className="text-slate-700">
                <LockOutlinedIcon style={{ fontSize: "20px" }} />
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">
                {t("Attempts", "Intentos", "尝试次数")}
              </p>
              <p className="text-lg font-semibold text-slate-900">
                {quizDetails?.retake_limit === null || quizDetails?.retake_limit === 0
                  ? `${quizDetails?.pass_attempt_count || 0} / ∞`
                  : `${quizDetails?.pass_attempt_count || 0} / ${quizDetails?.retake_limit}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Get Started Button */}
      <div
        className="w-full max-w-md px-6 py-4 rounded-lg bg-violet-700 text-white text-sm cursor-pointer mt-8 text-center font-medium"
        onClick={handleStartQuiz}
      >
        {t("Get Started", "Comenzar", "开始")}
      </div>
    </div>
  );

  const renderQuestion = () => {
    const question = quizDetails.learning_questions[currentQuestionIndex];
    return (
      <motion.div
        ref={questionContainerRef}
        animate={
          shake && !reducedMotion
            ? { x: [0, -8, 8, -6, 6, -3, 3, 0] }
            : { x: 0 }
        }
        transition={{ duration: 0.45 }}
        className={mobilePreview ? "w-full" : "w-[60%] sm:w-full"}
      >
        {/* Question Header */}
        <div className="mb-4">
          <span className="text-sm font-medium text-slate-500">
            {props.language === "chinese"
              ? `${currentQuestionIndex + 1} / ${quizDetails.learning_questions.length} 题`
              : `${t("Question", "Pregunta", "Question")} ${currentQuestionIndex + 1} ${t("of", "de", "of")} ${quizDetails.learning_questions.length}`}
          </span>
          <h4 className="text-lg text-slate-900 mt-2">{question.question_v2?.[0]}</h4>
          {question.question_v2?.[2] && (
            <div className="mt-3">
              <img
                src={question.question_v2?.[2]}
                alt="Question"
                className="rounded-lg max-h-64 object-cover"
              />
            </div>
          )}
        </div>

        {/* Answer Choices */}
        <div className="space-y-2">
          {question.answers.map(
            ([answer, is_correct, image_uuid, image_url], index) => {
              const answerId = image_uuid || answer;
              const isSelected = selectedAnswers.includes(answerId);

              let bgColor = "bg-slate-50";
              let textColor = "text-slate-700";
              let borderColor = "border-slate-200";
              let iconBgColor = "bg-white";
              let iconBorderColor = "border-slate-300";

              if (hasSubmitted && quizDetails.show_correct_answers) {
                if (isSelected && is_correct) {
                  bgColor = "bg-emerald-50";
                  textColor = "text-emerald-900";
                  borderColor = "border-emerald-300";
                  iconBgColor = "bg-emerald-600";
                } else if (isSelected && !is_correct) {
                  bgColor = "bg-rose-50";
                  textColor = "text-rose-900";
                  borderColor = "border-rose-300";
                  iconBgColor = "bg-rose-600";
                } else if (!isSelected && is_correct) {
                  bgColor = "bg-emerald-50";
                  textColor = "text-emerald-900";
                  borderColor = "border-emerald-300";
                  iconBgColor = "bg-emerald-600";
                }
              } else if (isSelected) {
                bgColor = "bg-violet-50";
                textColor = "text-violet-900";
                borderColor = "border-violet-300";
                iconBgColor = "bg-violet-600";
              }

              return (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${bgColor} ${borderColor} flex items-start gap-3 cursor-pointer`}
                  onPointerDown={handleTapStart}
                  onPointerUp={handleTapEnd(() =>
                    handleAnswerSelect(answer, image_uuid),
                  )}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {hasSubmitted && quizDetails.show_correct_answers ? (
                      is_correct ? (
                        <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center">
                          <CheckIcon style={{ fontSize: "16px", color: "white" }} />
                        </div>
                      ) : isSelected ? (
                        <div className="w-5 h-5 rounded-full bg-rose-600 flex items-center justify-center">
                          <CloseIcon style={{ fontSize: "16px", color: "white" }} />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-slate-300 bg-white"></div>
                      )
                    ) : isSelected ? (
                      <div className={`w-5 h-5 rounded-full ${iconBgColor} flex items-center justify-center`}>
                        <CheckIcon style={{ fontSize: "16px", color: "white" }} />
                      </div>
                    ) : (
                      <div className={`w-5 h-5 rounded-full border-2 ${iconBorderColor} bg-white`}></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={textColor}>{answer}</p>
                    {image_url && (
                      <div className="mt-2">
                        <img
                          src={image_url}
                          alt="Answer choice"
                          className="rounded-lg max-h-48 object-cover"
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            }
          )}
        </div>

        <div className="w-full mt-8">
          {!hasSubmitted ? (
            <div
              className={`w-full px-6 py-4 rounded-lg text-white text-sm text-center transition-all ${
                selectedAnswers.length === 0
                  ? "bg-gray-300 cursor-not-allowed opacity-60"
                  : "bg-violet-700 cursor-pointer hover:bg-violet-800"
              }`}
              onPointerDown={
                selectedAnswers.length > 0 ? handleTapStart : undefined
              }
              onPointerUp={
                selectedAnswers.length > 0
                  ? handleTapEnd(handleNextQuestion)
                  : undefined
              }
            >
              {t("Submit", "Enviar", "提交")}
            </div>
          ) : (
            <div
              className={`w-full px-6 py-4 rounded-lg text-white text-sm cursor-pointer flex items-center justify-center gap-x-1.5 ${
                feedback && quizDetails.show_correct_answers
                  ? feedback === "correct"
                    ? "bg-emerald-500"
                    : "bg-rose-500"
                  : "bg-violet-700"
              }`}
              onPointerDown={handleTapStart}
              onPointerUp={handleTapEnd(async () => {
                if (
                  currentQuestionIndex ===
                  quizDetails.learning_questions?.length - 1
                ) {
                  const ok = await recordAttemptIfNeeded();
                  // Gate advancement on successful persistence — if the
                  // server call rejects, the user stays on the last
                  // question so they can retry via Next without
                  // landing on a completed screen with no record.
                  if (!ok) return;
                }
                handleProceed();
              })}
            >
              {isSubmittingResults ? (
                <LoadSpinner size="small" />
              ) : (
                <>
                  <p>
                    {feedback && quizDetails.show_correct_answers
                      ? feedback === "correct"
                        ? t("Correct", "Correcto", "正确")
                        : t("Incorrect", "Incorrecto", "错误")
                      : currentQuestionIndex ===
                        quizDetails.learning_questions?.length - 1
                      ? t("Results", "Resultados", "结果")
                      : t("Next", "Siguiente", "下一题")}
                  </p>
                  <div>
                    {currentQuestionIndex ===
                      quizDetails.learning_questions?.length - 1 &&
                    (!feedback || !quizDetails.show_correct_answers) ? (
                      <ChecklistIcon style={{ fontSize: "20px" }} />
                    ) : (
                      <ChevronRightIcon style={{ fontSize: "20px" }} />
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  // Static preview of a question used for non-active slides during embla
  // transitions. The active question's render is handled by renderQuestion
  // (interactive, hits the global selectedAnswers / feedback state); this
  // version is read-only so a slide peeking into view during a swipe
  // doesn't accidentally show stale state from a different question.
  const renderInactiveQuestion = (slideQIdx) => {
    const question = quizDetails?.learning_questions?.[slideQIdx];
    if (!question) return null;
    return (
      <div className={mobilePreview ? "w-full" : "w-[60%] sm:w-full"}>
        <div className="mb-4">
          <span className="text-sm font-medium text-slate-500">
            {props.language === "chinese"
              ? `${slideQIdx + 1} / ${quizDetails.learning_questions.length} 题`
              : `${t("Question", "Pregunta", "Question")} ${slideQIdx + 1} ${t("of", "de", "of")} ${quizDetails.learning_questions.length}`}
          </span>
          <h4 className="text-lg text-slate-900 mt-2">
            {question.question_v2?.[0]}
          </h4>
          {question.question_v2?.[2] && (
            <div className="mt-3">
              <img
                src={question.question_v2?.[2]}
                alt="Question"
                className="rounded-lg max-h-64 object-cover"
              />
            </div>
          )}
        </div>
        {!question.is_drag_and_drop && (
          <div className="space-y-2">
            {(question.answers || []).map(
              ([answer, , , image_url], index) => (
                <div
                  key={index}
                  className="p-3 rounded-lg border bg-slate-50 border-slate-200 flex items-start gap-3"
                >
                  <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full border-2 border-slate-300 bg-white" />
                  <div className="flex-1">
                    <p className="text-slate-700">{answer}</p>
                    {image_url && (
                      <div className="mt-2">
                        <img
                          src={image_url}
                          alt="Answer choice"
                          className="rounded-lg max-h-48 object-cover"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </div>
    );
  };

  const renderNoQuestions = () => {
    return (
      <div className={mobilePreview ? "w-full" : "w-[60%] sm:w-full"}>
        <div className="w-full flex flex-col items-center justify-center">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-900">
            <InfoIcon />
          </div>
          <div className="w-72">
            <p className="mt-2 text-sm text-center">
              {t("There are no questions in this quiz", "No hay preguntas en este cuestionario", "此测验中没有题目")}
            </p>
          </div>
          {/* Close affordance — without this the user is stuck on this
              slide: there are no question slides to advance to and no
              completed slide to fall through to, and the embla
              forward-only guard prevents going back. */}
          <div
            className="mt-6 w-full max-w-xs px-6 py-3 rounded-lg bg-violet-700 text-white text-sm cursor-pointer text-center font-medium hover:bg-violet-800 transition-colors"
            onClick={() => {
              props.handleClose();
            }}
          >
            {t("Close", "Cerrar", "关闭")}
          </div>
        </div>
      </div>
    );
  };

  const renderCompleted = () => {
    const passScore = (score / quizDetails.learning_questions.length) * 100;
    const passed = passScore >= quizPassPercentage;
    const isTestOut = quizDetails?.gate_on_pass === false;

    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-6">
        {/* Status Icon/Badge */}
        <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
          passed ? 'bg-emerald-100' : 'bg-rose-100'
        }`}>
          <div className={`${passed ? 'text-emerald-600' : 'text-rose-600'}`}>
            {passed ? (
              <CheckIcon style={{ fontSize: "48px" }} />
            ) : (
              <CloseIcon style={{ fontSize: "48px" }} />
            )}
          </div>
        </div>

        {/* Main Result */}
        <h3 className={`text-4xl font-bold mt-6 ${
          passed ? 'text-emerald-700' : 'text-rose-700'
        }`}>
          {passed
            ? t("You Passed!", "¡Aprobaste!", "你通过了！")
            : t("You Did Not Pass", "No aprobaste", "你未通过")}
        </h3>

        {/* Score Card */}
        <div className="mt-8 bg-slate-50 rounded-xl p-8 w-full max-w-md">
          <div className="flex items-center justify-around">
            <div className="text-center">
              <p className="text-slate-500 text-sm mb-2">
                {t("Your Score", "Tu puntuación", "你的分数")}
              </p>
              <p className={`text-5xl font-bold ${
                passed ? 'text-emerald-600' : 'text-rose-600'
              }`}>
                {passScore.toFixed()}%
              </p>
            </div>

            {quizPassPercentage > 0 && (
              <>
                <div className="h-16 w-px bg-slate-300"></div>
                <div className="text-center">
                  <p className="text-slate-500 text-sm mb-2">
                    {t("Required", "Requerido", "要求")}
                  </p>
                  <p className="text-5xl font-bold text-slate-700">
                    {quizPassPercentage.toFixed()}%
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Questions breakdown */}
          <div className="mt-6 pt-6 border-t border-slate-200 text-center">
            <p className="text-slate-600">
              <span className="font-semibold text-slate-900">{score}</span>
              {' '}{t("out of", "de", "/")}{' '}
              <span className="font-semibold text-slate-900">
                {quizDetails.learning_questions.length}
              </span>
              {props.language === "chinese" ? " 题答对" : ` ${t("correct", "correctas", "correct")}`}
            </p>
          </div>
        </div>

        {/* Message */}
        <p className={`mt-6 text-center max-w-md ${
          passed ? 'text-emerald-700' : 'text-slate-600'
        }`}>
          {isTestOut
            ? passed
              ? t(
                "Great job! You can skip the sections this quiz covers.",
                "¡Excelente trabajo! Puedes omitir las secciones que cubre este cuestionario.",
                "做得很好！你可以跳过此测验涵盖的章节。"
              )
              : t(
                "You can continue. The sections this quiz covers are required for you.",
                "Puedes continuar. Las secciones que cubre este cuestionario son obligatorias para ti.",
                "你可以继续。此测验涵盖的章节是你必须完成的。"
              )
            : passed
            ? t(
              "Great job! You've successfully completed this quiz.",
              "¡Excelente trabajo! Has completado exitosamente este cuestionario.",
              "做得很好！你已成功完成此测验。"
            )
            : t(
              "Keep trying! Review the material and give it another shot.",
              "Sigue intentándolo. Revisa el material y vuelve a intentarlo.",
              "继续加油！复习材料后再试一次。"
            )}
        </p>

        {/* Action Buttons */}
        <div className="mt-8 w-full max-w-md space-y-3">
          {/* Review Answers Button - Only show if attemptUuid exists (quiz was taken, not previewed) */}
          {attemptUuid && (
            <div
              className="w-full px-6 py-4 rounded-lg bg-slate-100 text-slate-700 text-sm cursor-pointer text-center font-medium hover:bg-slate-200"
              onClick={() => setOpenReviewModal(true)}
            >
              {t("Review Answers", "Revisar respuestas", "查看答案")}
            </div>
          )}

          {/* Continue Button */}
          <div
            className="w-full px-6 py-4 rounded-lg bg-violet-700 text-white text-sm cursor-pointer text-center font-medium"
            onClick={() => {
              // Reset all state for next attempt. Continue closes the
              // modal, but state mutations may still flush before the
              // close lands and a future flow that keeps the modal open
              // between attempts (or re-opens without unmounting) needs
              // the completion-cycle refs reset too — otherwise the
              // next attempt's recordAttemptIfNeeded would short-circuit
              // on a stale "already recorded" guard and the attempt
              // would never reach the server.
              setStep("welcome");
              setCurrentQuestionIndex(0);
              setSelectedAnswers([]);
              setScore(0);
              setHasSubmitted(false);
              setFeedback(null);
              setQuestionResponses([]);
              setAttemptUuid(null);
              setIsSubmittingResults(false);
              attemptRecordedRef.current = false;
              hasFiredCompletionConfettiRef.current = false;
              lastSlideIdxRef.current = 0;

              // Close modal
              props.handleClose();

              if (props.type !== "Preview") {
                props.setRefreshSection(true);
              }
            }}
          >
            {t("Continue", "Continuar", "继续")}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
    <Modal
      open={props.open}
      onClose={() => {
        props.handleClose();
        setScore(0);
        if (step === "completed" && props.type !== "Preview" ) {
          props.setRefreshSection(true);
        }
      }}
      slotProps={
        mobilePreview
          ? {
              // Dark backdrop matches the swipe deck's preview surround
              // (bg-black/95) so the phone frame reads as a device on
              // stage. Default MUI backdrop on the desktop preview path.
              backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.95)" } },
            }
          : undefined
      }
    >
      <Box sx={style} ref={modalRef}>
        {/* Local confetti canvas — bound via confetti.create so particles
            render inside the modal's stacking context (the library's
            default canvas at z-100 sits behind this z-1300 modal).
            Pointer-events-none keeps it from swallowing answer clicks. */}
        <canvas
          ref={setCanvasRef}
          className="pointer-events-none"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            zIndex: 100,
          }}
        />
        {/* Desktop close X — hidden on mobile (replaced by the
            chrome-integrated circular button in the mobile bar). */}
        <div
          className={`absolute right-2 top-2 text-gray-500 cursor-pointer z-40 ${mobilePreview ? "hidden" : "sm:hidden"}`}
          onClick={() => {
            props.handleClose();
            setScore(0);
            if (step === "completed" && props.type !== "Preview") {
              props.setRefreshSection(true);
            }
          }}
        >
          <CloseIcon />
        </div>

        {/* Mobile chrome — TikTok / Stories style: segmented progress
            bars (one per question) + circular X, with a soft white
            backdrop fade so the chrome stays legible over scrolling
            content. Hidden on desktop where the inline header + dots
            chrome handles the same job. */}
        <div className={`${mobilePreview ? "block" : "hidden sm:block"} absolute inset-x-0 top-0 z-40 px-4 pt-3 pb-6 pointer-events-none`}>
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/85 to-transparent" />
          <div className="relative flex items-center gap-2">
            {slides.length > 1 ? (
              <div className="flex-1 flex gap-1">
                {slides.map((slide, i) => {
                  // Bar represents "user has reached this slide". TikTok's
                  // partial-fill is meant to animate over time as a story
                  // plays — quizzes have no timer for that to track, so a
                  // half-filled bar reads as a paused state rather than
                  // progress. Binary fill (visited or not) is unambiguous.
                  const activeIdx =
                    step === "welcome"
                      ? 0
                      : step === "completed"
                      ? slides.length - 1
                      : 1 + currentQuestionIndex;
                  const isFilled = i <= activeIdx;
                  return (
                    <div
                      key={i}
                      className="flex-1 h-[3px] rounded-full bg-slate-200 overflow-hidden"
                    >
                      <div
                        className="h-full bg-slate-900 transition-[width] duration-300"
                        style={{ width: isFilled ? "100%" : "0%" }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex-1" />
            )}
            <button
              type="button"
              onClick={() => {
                props.handleClose();
                setScore(0);
                if (step === "completed" && props.type !== "Preview") {
                  props.setRefreshSection(true);
                }
              }}
              className="pointer-events-auto w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-700 transition-colors"
              aria-label="Close"
            >
              <CloseIcon style={{ fontSize: 16 }} />
            </button>
          </div>
        </div>

        {!isLoading ? (
          <div className="w-full h-full flex flex-col items-center">
            {/* Desktop quiz title bar — hidden on mobile */}
            <div className={`py-5 w-full flex justify-center shadow ${mobilePreview ? "hidden" : "sm:hidden"}`}>
              <p className="text-slate-900 text-base">{quizDetails?.name}</p>
            </div>
            {/* Desktop progress dots / thin bar — hidden on mobile */}
            {step !== "welcome" ? (
              <div className={`w-full bg-slate-100 flex items-center justify-center shadow-inner ${mobilePreview ? "hidden" : "sm:hidden"}`}>
                <div className="flex items-center space-x-5">
                  {quizDetails?.learning_questions?.map((dot, index) => (
                    <div key={index}>
                      {index === currentQuestionIndex ? (
                        <div className="w-4 h-4 rounded-full border border-violet-700 flex items-center justify-center bg-slate-100 my-3">
                          <div className="w-2 h-2 rounded-full bg-violet-700"></div>
                        </div>
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-slate-600"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={`w-full h-2 bg-slate-100 shadow-inner ${mobilePreview ? "hidden" : "sm:hidden"}`}></div>
            )}
            {/* Embla viewport — vertical carousel wrapping welcome /
                question(s) / completed slides. State is the source of
                truth (handlers call setStep + setCurrentQuestionIndex);
                a useEffect mirrors that to embla.scrollTo. Touch + mouse
                drag also fire on('select') which mirrors back. The
                forward-only guard in on('select') snaps backward swipes
                forward, since previously-submitted questions can't be
                re-attempted. */}
            <div
              className="flex-grow w-full overflow-hidden"
              ref={emblaRef}
            >
              <div className="flex flex-col h-full">
                {slides.map((slide, sIdx) => {
                  const isActiveQuestion =
                    slide.kind === "question" &&
                    step === "question" &&
                    slide.idx === currentQuestionIndex;
                  return (
                    <div
                      key={sIdx}
                      className="relative flex-[0_0_100%] min-h-0 overflow-y-auto"
                    >
                      {/* Inner wrapper handles both layouts:
                          - Content fits viewport → min-h-full keeps the
                            wrapper at viewport height, justify-center
                            vertically centers the content (welcome /
                            completed / short questions look balanced).
                          - Content overflows → wrapper grows to content,
                            justify-center has no extra space to use, so
                            content sits at top, and the outer scroll
                            container reveals the rest (Submit button on
                            tall image-answer questions stays reachable). */}
                      <div className={`min-h-full flex flex-col items-center justify-center p-6 ${mobilePreview ? "pt-16" : "sm:pt-16"}`}>
                        {slide.kind === "welcome" && renderWelcome()}
                        {slide.kind === "no-questions" && renderNoQuestions()}
                        {slide.kind === "question" &&
                          (isActiveQuestion
                            ? quizDetails.learning_questions[slide.idx]
                                .is_drag_and_drop
                              ? (
                                <RenderQuestionDragAndDrop
                                  question={
                                    quizDetails.learning_questions[slide.idx]
                                  }
                                  selectedAnswers={selectedAnswers}
                                  setSelectedAnswers={setSelectedAnswers}
                                  hasSubmitted={hasSubmitted}
                                  feedback={feedback}
                                  handleNextQuestion={handleNextQuestion}
                                  handleProceed={handleProceed}
                                  currentQuestionIndex={currentQuestionIndex}
                                  totalQuestions={
                                    quizDetails.learning_questions.length
                                  }
                                  quizDetails={quizDetails}
                                  score={score}
                                  setScore={setScore}
                                  setFeedback={setFeedback}
                                  setHasSubmitted={setHasSubmitted}
                                  questionResponses={questionResponses}
                                  setQuestionResponses={setQuestionResponses}
                                  postLearningBlockUserPassed={
                                    props.post_learning_block_user_passed
                                  }
                                  recordAttemptIfNeeded={recordAttemptIfNeeded}
                                  employeeID={props.employeeID}
                                  type={props.type}
                                  language={props.language}
                                />
                              )
                              : renderQuestion()
                            : renderInactiveQuestion(slide.idx))}
                        {slide.kind === "completed" && renderCompleted()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full h-[400px]">
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <LoadSpinner />
            </div>
          </div>
        )}
      </Box>
    </Modal>
    {/* Quiz Review Modal */}
    <QuizReviewModal
      open={openReviewModal}
      handleClose={() => setOpenReviewModal(false)}
      attemptUuid={attemptUuid}
      homeCompany={props.homeCompany}
      language={props.language}
    />
    </>
  );
}

export default CompleteQuizModal;
